import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BoatCard } from "@/components/public/BoatCard";
import { Seo } from "@/components/Seo";
import { ImageSlot } from "@/components/ImageSlot";
import { api } from "@/lib/api";

export default function Inventory() {
  const [params, setParams] = useSearchParams();
  const [boats, setBoats] = useState([]);
  const [options, setOptions] = useState({ brands: [], boat_types: [], engine_makes: [] });
  const [loading, setLoading] = useState(true);

  const condition = params.get("condition") || "";
  const brand = params.get("brand") || "";
  const boat_type = params.get("boat_type") || "";
  const status = params.get("status") || "";
  const sort = params.get("sort") || "";
  const q = params.get("q") || "";

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value && value !== "all") next.set(key, value); else next.delete(key);
    setParams(next);
  };

  const load = useCallback(() => {
    setLoading(true);
    const qp = new URLSearchParams();
    if (condition) qp.set("condition", condition);
    if (brand) qp.set("brand", brand);
    if (boat_type) qp.set("boat_type", boat_type);
    if (status) qp.set("status", status);
    if (sort) qp.set("sort", sort);
    if (q) qp.set("q", q);
    api.get(`/boats?${qp.toString()}`).then((r) => setBoats(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [condition, brand, boat_type, status, sort, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/filters/options").then((r) => setOptions(r.data)).catch(() => {}); }, []);

  const title = condition === "new" ? "New Inventory" : condition === "used" ? "Used Inventory" : "All Inventory";

  return (
    <div data-testid="inventory-page">
      <Seo title={`${title} | MTM Marine Sales & Service, Bristol, TN`} description={`Browse ${title.toLowerCase()} at MTM Marine in Bristol, TN. Falcon Boats, Armada Pontoons, and quality used boats with certified service.`} />
      <section className="bg-mtm-charcoal text-white">
        <div className="absolute" />
        <div className="max-w-7xl mx-auto px-4 py-12">
          <p className="text-xs font-bold uppercase tracking-widest text-mtm-teal-bright font-mono">Bristol, Tennessee</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold font-heading tracking-tight mt-2">{title}</h1>
          <p className="text-slate-300 mt-2">{boats.length} boat{boats.length !== 1 ? "s" : ""} available</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-10 grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Filters */}
        <aside className="lg:col-span-1" data-testid="inventory-filters">
          <div className="bg-white rounded-xl border border-slate-200 p-5 sticky top-24">
            <div className="flex items-center gap-2 mb-4 text-slate-900 font-heading font-bold"><SlidersHorizontal size={18} /> Filters</div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Condition</Label>
                <Select value={condition || "all"} onValueChange={(v) => update("condition", v)}>
                  <SelectTrigger data-testid="filter-condition"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="used">Used</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Brand</Label>
                <Select value={brand || "all"} onValueChange={(v) => update("brand", v)}>
                  <SelectTrigger data-testid="filter-brand"><SelectValue placeholder="All Brands" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Brands</SelectItem>
                    {options.brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Boat Type</Label>
                <Select value={boat_type || "all"} onValueChange={(v) => update("boat_type", v)}>
                  <SelectTrigger data-testid="filter-boat-type"><SelectValue placeholder="All Types" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {options.boat_types.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={status || "all"} onValueChange={(v) => update("status", v)}>
                  <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any Status</SelectItem>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="en_route">En Route</SelectItem>
                    <SelectItem value="coming_soon">Coming Soon</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="sold">Sold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Search</Label>
                <Input defaultValue={q} placeholder="Model, keyword..." onKeyDown={(e) => { if (e.key === "Enter") update("q", e.target.value); }} data-testid="filter-search" />
              </div>
              <Button variant="outline" className="w-full rounded-full" onClick={() => setParams(new URLSearchParams())} data-testid="clear-filters-button">Clear Filters</Button>
            </div>
          </div>
        </aside>

        {/* Results */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-5">
            <span className="text-sm text-slate-500">{loading ? "Loading..." : `${boats.length} results`}</span>
            <Select value={sort || "newest"} onValueChange={(v) => update("sort", v === "newest" ? "" : v)}>
              <SelectTrigger className="w-52" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest Listings</SelectItem>
                <SelectItem value="price_asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc">Price: High to Low</SelectItem>
                <SelectItem value="year_desc">Year: Newest First</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {boats.length ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {boats.map((b, i) => <BoatCard key={b.id} boat={b} index={i} />)}
            </div>
          ) : !loading ? (
            <div className="text-center py-20">
              <ImageSlot label="No-Results.jpg" dimensions="" aspect="aspect-video" className="max-w-sm mx-auto" />
              <p className="text-slate-500 mt-6">No boats match your filters. <Link to="/contact" className="text-mtm-purple underline">Tell us what you're looking for</Link>.</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
